import React, { useCallback } from 'react';
import { Loading, ErrorMessage } from '@koopajs/mui';
import {
  DialogContentText,
  Button,
  Box,
  Container,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import { useLocale, useResourceDelete, useComponentVisibility } from '@koopajs/react';
import { Trans } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Close as CloseIcon } from '@mui/icons-material';
import { TrackEvent } from '@koopajs/app';
import { useConfirmDelete } from 'components/hooks/useConfirmDelete';
import { FormControllerTemp } from 'components/temp/FormControllerTemp';

export const i18nTransComponents: { [k: string]: React.ReactElement } = { div: <div /> };

export const DeleteDocumentFromPublishedAgenda: React.FC = () => {
  const confirmDeleteDocumentDialog = useComponentVisibility('confirmDeleteDocumentFromPublishedAgenda');

  const { documentId, path } = confirmDeleteDocumentDialog.getContext() as {
    documentId: string;
    path: string;
  };

  const { deleteResource, isProcessing, errorMessage } = useResourceDelete({
    path: path,
    id: documentId
  });

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { t } = useLocale();
  const keyPrefix = 'Dialogs.DeleteDocumentFromPublishedAgenda';

  const { hasUserTypedDeleteConfirmation, renderConfirmDeleteField, resetConfirmDeleteField } =
    useConfirmDelete({
      i18nKeyPrefix: keyPrefix + '.ConfirmDeleteField'
    });

  const handleCloseDialog = useCallback(() => {
    confirmDeleteDocumentDialog.setHidden();
    resetConfirmDeleteField();
  }, []);

  const handleOnSubmit = useCallback(async () => {
    const res = await deleteResource();

    if (res) {
      handleCloseDialog();

      enqueueSnackbar(t(keyPrefix + '.snackbarDeleteSuccess'), {
        variant: 'success',
        action: (key: any) => (
          <IconButton size="small" onClick={() => closeSnackbar(key)}>
            <CloseIcon sx={{ color: 'white' }} />
          </IconButton>
        )
      });
    } else {
      enqueueSnackbar(t(keyPrefix + '.snackbarDeleteFail'), {
        variant: 'error',
        action: (key: any) => (
          <IconButton size="small" onClick={() => closeSnackbar(key)}>
            <CloseIcon sx={{ color: 'white' }} />
          </IconButton>
        )
      });

      TrackEvent('published-agenda-document-delete-fail');
    }

    return true;
  }, [deleteResource, path, documentId]);

  return (
    <Dialog
      open={confirmDeleteDocumentDialog.isVisible}
      onClose={handleCloseDialog}
      fullWidth
      maxWidth="md"
      sx={{
        className: 'rr-block'
      }}
    >
      {isProcessing ? (
        <Stack
          sx={{ flexDirection: 'row', justifyContent: 'center', minHeight: '370px', alignItems: 'center' }}
        >
          <Container
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              p: 3
            }}
          >
            <Loading sx={{ backgroundColor: 'transparent', position: 'static' }} />
          </Container>
        </Stack>
      ) : (
        <Container
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 3
          }}
        >
          <ErrorMessage error={errorMessage} />

          <DialogTitle sx={{ textAlign: 'center' }}>
            {t(keyPrefix + '.title')}
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'grey'
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <FormControllerTemp onSubmit={handleOnSubmit} style={{ display: 'contents' }}>
            <DialogContent
              sx={{
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3
              }}
            >
              <DialogContentText>
                <Trans i18nKey={`${keyPrefix}.deleteDocument`} t={t} components={i18nTransComponents} />
              </DialogContentText>
              {renderConfirmDeleteField()}

              <Box sx={{ display: 'flex', mt: 1 }}>
                <Button sx={{ px: 7, mx: 1 }} variant="outlined" onClick={handleCloseDialog}>
                  {t('common:labelCancel')}
                </Button>
                <Button
                  color="error"
                  sx={{ px: 6.5, mx: 1 }}
                  variant="contained"
                  type="submit"
                  disabled={!hasUserTypedDeleteConfirmation}
                >
                  {t(keyPrefix + '.labelDelete')}
                </Button>
              </Box>
            </DialogContent>
          </FormControllerTemp>
        </Container>
      )}
    </Dialog>
  );
};
